import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Recycle, Clock, DollarSign, CheckCircle, Truck, Star, Users, Sparkles, Home, Gift } from 'lucide-react';
import { apiClient } from '../utils/api.js';
import junk1 from '../assets/junk-removal/junk1.jpg';
import junk2 from '../assets/junk-removal/junk2.jpg';
import junk3 from '../assets/junk-removal/junk3.jpg';
import junk4 from '../assets/junk-removal/junk4.jpg';
import junk5 from '../assets/junk-removal/junk5.jpg';
import junk6 from '../assets/junk-removal/junk6.jpg';
import SEO from '../components/SEO.js';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';
import '../styles/pages/JunkRemovalService.css';
import { sendContactEmail, isEmailNotificationsEnabled } from '../utils/email.js';

const JunkRemovalService = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'junk-removal',
    message: '',
    address: '',
    preferredDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const emailNotificationsEnabled = useMemo(() => isEmailNotificationsEnabled, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await apiClient.post('/api/contact', formData);
      if (emailNotificationsEnabled) {
        try {
          await sendContactEmail({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            message: formData.message,
            preferredDate: formData.preferredDate || 'Not provided',
            address: formData.address || 'Not provided'
          });
          setSubmitStatus('success');
        } catch (emailError) {
          console.warn('EmailJS send failed:', emailError);
          setSubmitStatus('partial');
        }
      } else {
        setSubmitStatus('success');
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: 'junk-removal',
        message: '',
        address: '',
        preferredDate: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sample Yelp reviews data for junk removal
  const reviews = [
    {
      id: 1,
      name: "David K.",
      rating: 5,
      date: "1 week ago",
      text: "Excellent service! They removed all our old furniture quickly and efficiently. The team was professional and the price was very fair. Highly recommend!",
      service: "Furniture Removal"
    },
    {
      id: 2,
      name: "Lisa P.",
      rating: 5,
      date: "2 weeks ago",
      text: "Amazing junk removal service. They came same day and cleared out our entire garage. Very professional and eco-friendly disposal. Will use again!",
      service: "Garage Cleanout"
    },
    {
      id: 3,
      name: "Robert M.",
      rating: 5,
      date: "3 weeks ago",
      text: "Best junk removal company in town! They handled our construction debris removal perfectly. Fast, reliable, and affordable. Five stars!",
      service: "Construction Debris"
    }
  ];

  const galleryPhotos = [
    {
      src: junk1,
      alt: 'Trash enclosure cleaned out during junk removal service'
    },
    {
      src: junk2,
      alt: 'Garage cleanup before and after comparison'
    },
    {
      src: junk3,
      alt: 'Old furniture removal from living room'
    },
    {
      src: junk4,
      alt: 'Construction debris hauled away from driveway'
    },
    {
      src: junk5,
      alt: 'Yard waste cleanup with junk removal crew'
    },
    {
      src: junk6,
      alt: 'Estate cleanout showing cleared interior space'
    }
  ];

  return (
    <>
      <SEO
        title="Fast & Reliable Junk Removal Services - Same Day Pickup | SAP Movers"
        description="Professional junk removal services including furniture, appliances, and construction debris. Same-day pickup available. Eco-friendly disposal and competitive pricing."
        keywords="junk removal, furniture removal, appliance disposal, construction debris, same day pickup, eco-friendly disposal"
        canonical="https://sapmovers.com/junk-removal"
        ogTitle="Fast & Reliable Junk Removal Services - SAP Movers"
        ogDescription="Professional junk removal with same-day pickup. Eco-friendly disposal and competitive pricing."
      />

      <Header />

      {/* Hero Section */}
      <section className="hero">
        <h1>Fast & Reliable Junk Removal</h1>
        <p>Same-day pickup available for furniture, appliances, and construction debris. Eco-friendly disposal and competitive pricing.</p>
        <Link to="/contact" className="cta-button">Schedule Pickup</Link>
      </section>

      {/* Services Offered */}
      <section className="services">
        <h2>Our Junk Removal Services</h2>
        <div className="services-grid">
          <div className="service-card">
            <Trash2 size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Furniture Removal</h3>
            <p>Old sofas, mattresses, tables, and chairs. We safely remove and dispose of all types of furniture.</p>
          </div>
          
          <div className="service-card">
            <Truck size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Appliance Disposal</h3>
            <p>Refrigerators, washers, dryers, and other appliances. We handle heavy items safely and responsibly.</p>
          </div>
          
          <div className="service-card">
            <Trash2 size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Construction Debris</h3>
            <p>Renovation waste, drywall, lumber, and building materials. We clean up your construction site.</p>
          </div>
          
          <div className="service-card">
            <Trash2 size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Yard Waste</h3>
            <p>Branches, leaves, grass clippings, and other yard debris. We keep your property clean and tidy.</p>
          </div>
          
          <div className="service-card">
            <Recycle size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Electronics Recycling</h3>
            <p>Computers, TVs, and electronic devices. We ensure proper recycling and data security.</p>
          </div>
          
          <div className="service-card">
            <Trash2 size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Estate Cleanouts</h3>
            <p>Complete property cleanouts for estates, foreclosures, and hoarding situations. Compassionate service.</p>
          </div>

          <div className="service-card">
            <Gift size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Donation Pickups</h3>
            <p>We separate reusable items and deliver them to local charities, giving your belongings a second life.</p>
          </div>
          
          <div className="service-card">
            <Sparkles size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Property Cleanouts</h3>
            <p>Full-service support from packing and loading to disposal and sweep-up for garages, attics, and basements.</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="services" style={{ background: '#f8fafc' }}>
        <h2>Why Choose Our Junk Removal?</h2>
        <div className="services-grid">
          <div className="service-card">
            <Clock size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Same Day Service</h3>
            <p>Fast and reliable pickup service. We can often schedule same-day or next-day pickup.</p>
          </div>
          
          <div className="service-card">
            <Recycle size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Eco-Friendly Disposal</h3>
            <p>We recycle and donate whenever possible. Responsible disposal that's good for the environment.</p>
          </div>
          
          <div className="service-card">
            <CheckCircle size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>No Hidden Fees</h3>
            <p>Transparent pricing with upfront quotes. No surprises or hidden charges on your final bill.</p>
          </div>
          
          <div className="service-card">
            <DollarSign size={48} color="var(--icon-primary)" style={{ marginBottom: '1rem' }} />
            <h3>Competitive Pricing</h3>
            <p>Affordable rates with free estimates. We provide the best value for professional junk removal.</p>
          </div>
        </div>
      </section>

      {/* Works Section */}
      <section className="works-section">
        <div className="works-container">
          <h2>Junk Removal Gallery</h2>
          <p>Before-and-after looks at clutter turned clean</p>
          <div className="works-gallery">
            {galleryPhotos.map((photo, index) => (
              <div key={photo.src} className="gallery-card">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="gallery-image"
                  loading={index < 2 ? 'eager' : 'lazy'}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="reviews-section">
        <div className="reviews-container">
          <div className="reviews-header">
            <h2>What Our Customers Say</h2>
            
            
          </div>
          
          <div className="reviews-grid">
            {reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-name">{review.name}</div>
                    <div className="review-date">{review.date}</div>
                  </div>
                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16} 
                        color={i < review.rating ? "var(--icon-primary)" : "#e5e7eb"} 
                        fill={i < review.rating ? "var(--icon-primary)" : "none"}
                      />
                    ))}
                  </div>
                </div>
                <div className="review-service">{review.service}</div>
                <p className="review-text">"{review.text}"</p>
                <div className="review-source">
                  <span>Verified on Yelp</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="reviews-cta">
           
            <a
              href="https://www.yelp.com/biz/ken-s-green-junk-removal-irvine?osq=Junk+Removal&override_cta=Get+pricing"
              className="yelp-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Star size={16} />
              <span>View All Reviews</span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="contact-form">
        <h2>Schedule Your Junk Removal</h2>
        <form onSubmit={handleSubmit}>
          <div className="stack-on-mobile">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="stack-on-mobile">
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="preferredDate">Preferred Pickup Date</label>
              <input
                type="date"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Pickup Address *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Where should we pick up the items?"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Item Description *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              placeholder="Describe the items you need removed (furniture, appliances, etc.)"
              required
            />
          </div>
          
          {submitStatus === 'success' && (
            <div className="form-status success">
              ✓ Thank you! We'll contact you soon to schedule your pickup.
            </div>
          )}

          {submitStatus === 'partial' && (
            <div className="form-status warning">
              ✓ Request saved, but email notification failed. We'll follow up manually.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="form-status error">
              ✗ Sorry, there was an error. Please try again or call us directly.
            </div>
          )}
          
          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Schedule Junk Removal'}
          </button>
        </form>
      </section>

      <Footer />
    </>
  );
};

export default JunkRemovalService;
